import fetch from 'cross-fetch';
import createHttpError from 'http-errors';
import { get, identity } from 'lodash';
import qs from 'qs';

import { getErrorMessage } from 'queries/utils';

interface Logger {
    log: (...args: unknown[]) => void;
    info: (...args: unknown[]) => void;
    warn: (...args: unknown[]) => void;
    error: (...args: unknown[]) => void;
    debug: (...args: unknown[]) => void;
}

interface RequestOptions {
    path: string;
    method: string;
    headers?: Record<string, string>;
    search?: object;
    signal?: AbortSignal;
    body?: BodyInit | object;
}

type ExposedRequestOptions = Pick<RequestOptions, 'search' | 'signal' | 'body'>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyResponse = any;

type Init = RequestInit & { headers: Headers };

interface ConstructorOptions {
    logger?: Logger;
    baseUrl?: string;
    transform?: (init: Init) => Init;
}

export class RequestAgent {
    public static readonly STATUSES = {
        PAYMENT_REQUIRED: 402
    };
    private static jwtPromise: Promise<string> | null;

    protected logger: Logger;
    protected baseUrl: string;
    protected transform: (init: Init) => Init;

    constructor(options?: ConstructorOptions) {
        this.logger = options?.logger ?? console;
        this.baseUrl = options?.baseUrl ?? '';
        this.transform = options?.transform ?? identity;
    }

    protected isJson(response: Response) {
        return (
            response.headers.get('content-type')?.includes('application/json') &&
            response.headers.get('content-length') !== '0'
        );
    }

    protected buildUrl(path: string) {
        return [this.baseUrl.replace(/\/+$/, ''), path.replace(/^\/+/, '')].join('/');
    }

    protected async buildError(response: Response) {
        const fromJson = await response
            .json()
            .then((json: Error) => createHttpError(response.status, getErrorMessage(json), json))
            .catch(() => null);

        if (fromJson) {
            return fromJson;
        }

        const fromText = await response
            .text()
            .then((text) => createHttpError(response.status, text))
            .catch(() => null);

        if (fromText) {
            return fromText;
        }

        return createHttpError(response.status, response.statusText || 'Network response was not ok');
    }

    protected async getJwt() {
        RequestAgent.jwtPromise ||= this.request({
            method: 'post',
            path: '/api/v3/JwtIssuer/getsessiontoken/'
        }).then((response: object) => get(response, ['data', 'token'], ''));

        return RequestAgent.jwtPromise.catch(() => {
            RequestAgent.jwtPromise = null;
            return '';
        });
    }

    protected async request<T>(options: RequestOptions): Promise<T> {
        const headers = new Headers();

        headers.append('Accept', 'application/json');

        if (options.headers) {
            for (const header in options.headers) {
                headers.append(header, options.headers[header]);
            }
        }

        if (typeof options.body === 'object' && options.body !== null) {
            headers.set('Content-Type', 'application/json');
        }

        const url = this.buildUrl(
            options.search
                ? options.path + qs.stringify(options.search, { addQueryPrefix: true, indices: false, skipNulls: true })
                : options.path
        );

        const body =
            typeof options.body === 'object' && options.body !== null ? JSON.stringify(options.body) : options.body;

        const init = this.transform({
            method: options.method,
            body,
            headers,
            signal: options.signal
        });

        const response = await fetch(url, init);

        if (response.ok) {
            return this.isJson(response) ? response.json() : response;
        } else {
            const error = await this.buildError(response);

            this.logger.error(error.message, {
                error,
                url,
                body,
                source: 'network',
                prefix: 'queryFn'
            });

            throw error;
        }
    }

    public get public() {
        return {
            get: <T = AnyResponse>(path: string, options?: ExposedRequestOptions) =>
                this.request<T>({ path, method: 'get', ...options }),
            post: <T = AnyResponse>(path: string, options: ExposedRequestOptions) =>
                this.request<T>({ path, method: 'post', ...options }),
            put: <T = AnyResponse>(path: string, options: ExposedRequestOptions) =>
                this.request<T>({ path, method: 'put', ...options }),
            delete: <T = AnyResponse>(path: string, options: ExposedRequestOptions) =>
                this.request<T>({ path, method: 'delete', ...options })
        };
    }

    public get jwt() {
        return {
            get: <T = AnyResponse>(path: string, options?: ExposedRequestOptions) =>
                this.getJwt().then((token) =>
                    this.request<T>({ path, method: 'get', ...options, headers: { Authorization: `Bearer ${token}` } })
                ),
            post: <T = AnyResponse>(path: string, options: ExposedRequestOptions) =>
                this.getJwt().then((token) =>
                    this.request<T>({ path, method: 'post', ...options, headers: { Authorization: `Bearer ${token}` } })
                )
        };
    }

    public get csrf() {
        return (csrfPath: string) => {
            return {
                get: <T = AnyResponse>(path: string, options?: ExposedRequestOptions) =>
                    this.request({ method: 'get', path: csrfPath }).then((response: object) =>
                        this.request<T>({
                            path,
                            method: 'get',
                            ...options,
                            search: { ...options?.search, token: get(response, ['data', 'token'], '') }
                        })
                    ),
                post: <T = AnyResponse>(path: string, options: ExposedRequestOptions) =>
                    this.request({ method: 'get', path: csrfPath }).then((response: object) =>
                        this.request<T>({
                            path,
                            method: 'post',
                            ...options,
                            body: { ...(options.body as object), token: get(response, ['data', 'token'], '') }
                        })
                    ),
                put: <T = AnyResponse>(path: string, options: ExposedRequestOptions) =>
                    this.request({ method: 'get', path: csrfPath }).then((response: object) =>
                        this.request<T>({
                            path,
                            method: 'put',
                            ...options,
                            body: { ...(options.body as object), token: get(response, ['data', 'token'], '') }
                        })
                    ),
                delete: <T = AnyResponse>(path: string, options: ExposedRequestOptions) =>
                    this.request({ method: 'get', path: csrfPath }).then((response: object) =>
                        this.request<T>({
                            path,
                            method: 'delete',
                            ...options,
                            body: { ...(options.body as object), token: get(response, ['data', 'token'], '') }
                        })
                    )
            };
        };
    }
}
